// Responsible Infrastructure, single content engine.
// UI language: "Impact Stories". Underlying collection name: esgActivities (matches future DB).
// One story feeds: Impact Wall, Latest Activity, UK Map, Project Explorer, People profiles, Related content.

import { assets } from "./mock";

export const categories = {
  Environment: { icon: "Leaf", color: "#F5D000" },
  People: { icon: "Users", color: "#FFE34D" },
  Governance: { icon: "Scale", color: "#e8e8ec" },
  Community: { icon: "HeartHandshake", color: "#F5D000" },
  Safety: { icon: "ShieldCheck", color: "#FFE34D" },
  Innovation: { icon: "Lightbulb", color: "#e8e8ec" }
};

// Hero media rotation, add a video object here and it automatically joins the rotation.
export const heroMedia = [
  { type: "video", src: "https://customer-assets.emergentagent.com/job_polished-video-hero/artifacts/qhstkoi7_R%26C%20Sneaky%20peak%20into%20whats%20to%20come.mp4", poster: assets.aerial, label: "Live site overview" },
  { type: "image", src: assets.aerial, label: "Bridge works from above" },
  { type: "image", src: assets.sparks, label: "Steel fabrication" },
  { type: "image", src: assets.highway, label: "Highway structures" },
  { type: "image", src: assets.garden, label: "Community landscaping" }
];

// ================= IMPACT STORIES (db: esgActivities) =================
export const esgActivities = [
  {
    id: "giv1-initiative",
    title: "GIV£1: A Pound a Week That Changes Lives",
    date: "2026-06-01",
    categories: ["Community", "People"],
    tags: ["charity", "community", "people"],
    project: "GIV£1 Monthly Charity Initiative",
    client: "A different local charity every month",
    location: { name: "Erdington & Aston, Birmingham", x: 107, y: 211, group: "Charity" },
    people: ["Sam Drew", "Alex Colesby", "Aileen Bicknell"],
    summary: "Over 90% of the R&C workforce donate £1 a week from payroll. The company tops the pot up to £500 every month, and staff nominate a different local charity to receive it.",
    body: "GIV£1 started with one operative asking how he could personally give to the company's foodbank pledge. That conversation became a scheme the whole business joined: staff donate £1 per week straight from payroll, the company tops the total up to £500 each month, and every month a member of the team nominates a local charity close to their heart to receive it. More than 90% of the workforce signed up. The first recipient was Breast Friends in Sutton Coldfield, nominated for a Senior Foreman's wife going through breast cancer treatment, and the scheme has supported a different local cause every month since. It runs alongside our long-standing partnership with Erdington Foodbank, which we have topped up with monthly provisions since November 2020.",
    image: assets.garden,
    quote: "This really opened my eyes to what we could achieve collectively as a business.",
    size: "large",
    featured: true
  },
  {
    id: "three-peaks-challenge",
    title: "Three Peaks. 24 Hours. Mates in Mind.",
    date: "2026-06-13",
    categories: ["Community", "People"],
    tags: ["charity", "community", "people"],
    project: "National Three Peaks Challenge",
    client: "Fundraising for Mates in Mind",
    location: { name: "Ben Nevis · Scafell Pike · Snowdon", x: 56, y: 62, group: "Charity" },
    people: [],
    summary: "The R&C team took on the highest peaks in Scotland, England and Wales in 24 hours, smashing their £2,000 target for Mates in Mind and pushing towards £3,000.",
    body: "Months of training, preparation and teamwork came down to one 24-hour window: Ben Nevis, Scafell Pike and Snowdon, back to back. The R&C team set off from the Ben Nevis Visitor Centre at 17:00 and pushed through the climbs, the travelling and the tired legs, summiting Snowdon in driving rain with the R&C flags flying. The challenge raised funds for Mates in Mind, the charity tackling mental health in construction, with the team's original £2,000 target passed before boots even hit the first trail and a stretch goal of £3,000 in sight. A massive thank you to every sponsor, donor and supporter who backed the team.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_polished-video-hero/artifacts/afyoyesq_3%20peaks%20challenge.png",
    quote: "Be a mate. Be the change.",
    size: "tall",
    featured: true
  },
  {
    id: "cressbrook-frp",
    title: "A Bridge Built From 26,000 Bottles",
    date: "2023-11-08",
    categories: ["Environment", "Innovation"],
    tags: ["materials", "carbon", "wildlife"],
    project: "Cressbrook Mill FRP Footbridge",
    client: "Derbyshire County Council",
    location: { name: "Cressbrook, Peak District", x: 112, y: 178, group: "Environmental" },
    people: ["Alex Colesby"],
    summary: "A corroded steel crossing replaced with a Fibre Reinforced Polymer bridge made from ~26,000 recycled plastic bottles, a 120-year, low-maintenance asset.",
    body: "Located in a remote, environmentally sensitive corner of the Peak District National Park, the failing bridge at Cressbrook Mill needed a solution that would last generations without ongoing intervention. We installed a Fibre Reinforced Polymer structure manufactured from approximately 26,000 recycled plastic bottles, transported through some of the narrowest access routes in the Peaks and lifted into place in a single specialist crane operation. The result: a 120-year design life, hardwood handrails, minimal maintenance and a template for low-carbon structures across our portfolio.",
    image: assets.cressbrook,
    quote: "This bridge is our materials strategy made visible.",
    size: "large",
    featured: true
  },
  {
    id: "braunstone-watercourse",
    title: "Protecting a Live Watercourse at Braunstone",
    date: "2024-01-18",
    categories: ["Environment", "Safety"],
    tags: ["wildlife", "waste"],
    project: "Braunstone Culvert Replacement",
    client: "Leicester City Council",
    location: { name: "Braunstone, Leicester", x: 128, y: 205, group: "Projects" },
    people: ["Arron Roberts"],
    summary: "Silt control, temporary water management and full ecological protection while replacing a failing culvert inside an active watercourse.",
    body: "Replacing a deteriorated culvert at Mossdale Meadows meant working inside a live watercourse with restricted access and poor ground. Temporary water management, silt control systems and ecological watching briefs ran throughout the programme. The new precast concrete box culvert improves hydraulic capacity and cuts flood risk for the surrounding community, delivered without a single environmental incident.",
    image: assets.braunstone,
    size: "standard",
    featured: false
  },
  {
    id: "droitwich-active-travel",
    title: "Safer, Healthier Streets for Droitwich",
    date: "2024-02-28",
    categories: ["Community", "People"],
    tags: ["community", "people"],
    project: "Droitwich Health Corridor",
    client: "Worcestershire County Council",
    location: { name: "Droitwich, Worcestershire", x: 96, y: 215, group: "Community" },
    people: ["Duncan Braybrooke"],
    summary: "New footways, kerbing and drainage connecting healthcare, homes and amenities, phased carefully to keep the town moving.",
    body: "The Droitwich Health Corridor connects healthcare facilities, residential areas and local amenities with safer routes for pedestrians and cyclists. We delivered footways, kerbing, surfacing and drainage upgrades in carefully sequenced phases, coordinating with residents and businesses so the town kept moving throughout. The scheme now supports healthier travel choices across Worcestershire.",
    image: assets.droitwich,
    size: "tall",
    featured: false
  },
  {
    id: "hvo-fleet-trial",
    title: "HVO Fuel Trial Begins at Spaghetti Junction",
    date: "2026-04-22",
    categories: ["Environment", "Innovation"],
    tags: ["fleet", "carbon", "energy"],
    project: "Fleet Decarbonisation Programme",
    client: "Internal",
    location: { name: "Spaghetti Junction Depot, Birmingham", x: 104, y: 210, group: "Environmental" },
    people: ["Laura Toy", "Richard Toy"],
    summary: "Hydrotreated Vegetable Oil is now fuelling part of our depot fleet, the first step in our measured route to lower-carbon operations.",
    body: "Our transport team has switched an initial group of depot vehicles to Hydrotreated Vegetable Oil (HVO), with fuel and telematics data captured from day one so genuine carbon savings can be verified rather than estimated. Findings from the trial will shape the phased transition across the wider fleet and plant. No savings figures will be published until the data has been independently checked.",
    image: assets.highway,
    size: "standard",
    featured: true
  },
  {
    id: "carbon-baseline",
    title: "Measuring Before Claiming: Our Carbon Baseline",
    date: "2026-05-20",
    categories: ["Environment", "Governance"],
    tags: ["carbon", "reporting", "data"],
    project: "Carbon Baseline Programme",
    client: "Internal",
    location: { name: "Company-wide, Birmingham HQ", x: 104, y: 212, group: "Environmental" },
    people: ["Alex Colesby", "Laura Toy"],
    summary: "Fuel, plant, energy and travel data is being collected across every site to establish a verified Scope 1, 2 & 3 carbon baseline.",
    body: "Most contractors publish carbon claims first and verify later. We are doing it the other way round. Fuel records, plant hours, depot energy and business travel are being logged across every live site to build a complete Scope 1, 2 and 3 baseline. Once verified, this baseline anchors our published reduction pathway to Net Zero by 2040, and every future claim will trace back to it.",
    image: assets.aerial,
    size: "standard",
    featured: false
  },
  {
    id: "svr-heritage",
    title: "Heritage Craft at Severn Valley Railway",
    date: "2023-07-15",
    categories: ["Community", "Innovation"],
    tags: ["community", "materials"],
    project: "Severn Valley Railway Footbridge",
    client: "Severn Valley Railway",
    location: { name: "Bridgnorth, Shropshire", x: 88, y: 208, group: "Projects" },
    people: ["Matt Cain"],
    summary: "A heritage-sympathetic footbridge for one of the UK's best-loved railways, modern standards, Victorian character.",
    body: "Bridgnorth Station's listed Victorian environment demanded a structure that met modern standards without breaking the spell. Our fabrication team produced a steel footbridge sympathetic to the station's architecture, installed in phases around the railway's operating timetable so services never stopped. Heritage infrastructure is community infrastructure, and this crossing will serve visitors for decades.",
    image: assets.team,
    size: "standard",
    featured: false
  },
  {
    id: "apprentice-pathway",
    title: "Building the Fourth Generation",
    date: "2026-03-10",
    categories: ["People", "Community"],
    tags: ["training", "people"],
    project: "Apprenticeship Pathway",
    client: "Internal",
    location: { name: "Birmingham & local colleges", x: 106, y: 214, group: "Training" },
    people: ["Aileen Bicknell", "Sam Drew"],
    summary: "A structured apprenticeship pathway is taking shape with local colleges, creating trades and engineering routes into a 69-year-old family business.",
    body: "Three generations of craftsmen have built R&C. The fourth is being planned deliberately: a structured apprenticeship pathway developed with Birmingham colleges, covering site trades, steel fabrication and engineering. The first placements are targeted for 2027, with mentoring from the same team that delivered our award-winning structures.",
    image: assets.sparks,
    size: "tall",
    featured: false
  },
  {
    id: "safety-stand-down",
    title: "The Day Every Site Stopped",
    date: "2026-02-14",
    categories: ["Safety", "People"],
    tags: ["safety-systems", "people"],
    project: "Company-wide Safety Stand-down",
    client: "Internal",
    location: { name: "All active sites", x: 104, y: 208, group: "Training" },
    people: ["Arron Roberts"],
    summary: "Every R&C site paused for a behavioural safety stand-down, because anyone on our sites can stop the job, and everyone should know why.",
    body: "Once a year, every site stops at the same time. Tools down, engines off. Our Health & Safety Manager leads a company-wide stand-down covering the near-misses, the lessons and the behaviours that keep people safe. The message never changes: anyone can stop the job, and nobody digs alone. It's why our safety culture is the first thing long-standing clients mention.",
    image: assets.team,
    size: "standard",
    featured: true
  },
  {
    id: "supplier-charter",
    title: "An Evidence-First Supply Chain Charter",
    date: "2026-03-30",
    categories: ["Governance"],
    tags: ["ethics", "supply-chain"],
    project: "Supplier ESG Charter",
    client: "Internal",
    location: { name: "Company-wide, Birmingham HQ", x: 102, y: 213, group: "Projects" },
    people: ["Richard Farrand", "Alex Colesby"],
    summary: "Modern slavery, fair payment and environmental standards, drafted into one charter now under review with our supply chain partners.",
    body: "Responsibility flows down the supply chain or it doesn't exist. Our draft Supplier Charter sets out expectations on modern slavery, prompt payment, waste handling and environmental practice for every subcontractor and supplier. It is currently with supply chain partners for consultation, with full rollout targeted for 2027.",
    image: assets.sparks,
    size: "standard",
    featured: false
  },
  {
    id: "iso-14001",
    title: "Environmental Management, Certified",
    date: "2010-06-01",
    categories: ["Governance", "Environment"],
    tags: ["accreditations", "reporting"],
    project: "ISO 14001 Certification",
    client: "Internal",
    location: { name: "Birmingham HQ", x: 103, y: 211, group: "Environmental" },
    people: ["Alex Colesby"],
    summary: "ISO 14001 environmental management has underpinned how we plan, deliver and audit site environmental controls since 2010.",
    body: "Certification is only useful if it changes behaviour. Since 2010, ISO 14001 has structured how we assess environmental risk on every scheme, from silt control on watercourse jobs to waste segregation at the depot. Annual external audits keep the system honest, and the certificate is available to every client in our document library.",
    image: assets.garden,
    size: "standard",
    featured: false
  },
  {
    id: "charity-partnership",
    title: "Scoping Our Community Programme",
    date: "2026-05-02",
    categories: ["Community"],
    tags: ["charity", "community"],
    project: "Community Programme 2026",
    client: "Internal",
    location: { name: "Aston & Erdington, Birmingham", x: 106, y: 209, group: "Charity" },
    people: ["Laura Toy", "Aileen Bicknell"],
    summary: "Charity partnerships and school engagement around our Aston depot are being scoped for launch later this year.",
    body: "Our depot has sat beneath Spaghetti Junction for four decades, the neighbourhoods around it are part of who we are. We're scoping a structured community programme: charity partnerships, school engagement on engineering careers and volunteering days for the team. Partners will be announced as agreements are confirmed; nothing is published here until it is real.",
    image: assets.garden,
    size: "standard",
    featured: false
  },
  {
    id: "ditchford-heritage",
    title: "Eighty More Years for Ditchford Bridge",
    date: "2021-09-20",
    categories: ["Environment", "Innovation"],
    tags: ["materials", "community"],
    project: "Ditchford Bridge Restoration",
    client: "Northamptonshire Highways",
    location: { name: "Ditchford, Northamptonshire", x: 132, y: 220, group: "Projects" },
    people: ["Peter Gutteridge"],
    summary: "Hand-finished masonry restoration with hidden steel reinforcement, heritage preserved, service life extended by 80+ years.",
    body: "Restoration is the most sustainable form of construction: the greenest structure is the one you don't demolish. At Ditchford we combined hand-finished masonry repair with concealed steel reinforcement and modern waterproofing, preserving the bridge's historic character while extending its structural life by more than eighty years.",
    image: assets.team,
    size: "standard",
    featured: false
  }
];

// ================= WHEEL =================
export const wheel = [
  {
    key: "environment",
    label: "Environment",
    color: "#F5D000",
    subs: [
      { key: "carbon", label: "Carbon" },
      { key: "fleet", label: "Fleet" },
      { key: "waste", label: "Waste" },
      { key: "materials", label: "Materials" },
      { key: "energy", label: "Energy" },
      { key: "wildlife", label: "Wildlife" }
    ]
  },
  {
    key: "social",
    label: "Social",
    color: "#FFE34D",
    subs: [
      { key: "people", label: "People" },
      { key: "training", label: "Training" },
      { key: "community", label: "Community" },
      { key: "charity", label: "Charity" }
    ]
  },
  {
    key: "governance",
    label: "Governance",
    color: "#efefe9",
    subs: [
      { key: "accreditations", label: "Accreditations" },
      { key: "reporting", label: "Reporting" },
      { key: "ethics", label: "Ethics" },
      { key: "supply-chain", label: "Supply Chain" },
      { key: "safety-systems", label: "Safety Systems" },
      { key: "data", label: "Data" }
    ]
  }
];

// ================= JOURNEY =================
export const journey = [
  {
    id: "j1957", year: "1957", title: "Founded in Birmingham",
    status: "Completed",
    text: "R&C established as a family civil engineering contractor. Responsibility started as a family value long before it had a name.",
    image: assets.team, docs: []
  },
  {
    id: "j2010", year: "2010", title: "ISO 14001 Certified",
    status: "Completed",
    text: "Environmental management certified and externally audited every year since, the backbone of our site environmental controls.",
    image: assets.garden, docs: ["Environmental Management Certificate"], storyId: "iso-14001"
  },
  {
    id: "j2012", year: "2012", title: "ICE Innovation of the Year",
    status: "Completed",
    text: "The Institution of Civil Engineers recognised our innovative delivery, proof that a regional family firm can lead the industry.",
    image: assets.sparks, docs: []
  },
  {
    id: "j2023", year: "2023", title: "The Recycled-Bottle Bridge",
    status: "Completed",
    text: "26,000 recycled plastic bottles became a 120-year FRP footbridge in the Peak District, our low-carbon materials strategy made visible.",
    image: assets.cressbrook, docs: [], storyId: "cressbrook-frp"
  },
  {
    id: "jtoday", year: "Today", title: "Carbon Baseline Programme",
    status: "On Track",
    text: "Scope 1, 2 & 3 data collection is live across every site. We measure before we claim, no figure gets published without evidence.",
    image: assets.aerial, docs: [], storyId: "carbon-baseline"
  },
  {
    id: "j2030", year: "2030", title: "30% Carbon Intensity Cut",
    status: "Planned",
    text: "Fleet transition, HVO fuels and low-carbon materials targeted to cut carbon intensity by 30% against our verified baseline.",
    image: assets.highway, docs: []
  },
  {
    id: "j2040", year: "2040", title: "Net Zero Operations",
    status: "Planned",
    text: "Our destination: net zero operational carbon by 2040, with a published pathway anchored to the verified baseline.",
    image: assets.aerial, docs: []
  }
];

// ================= PEOPLE =================
export const people = [
  {
    id: "alex-colesby", name: "Alex Colesby", initials: "AC",
    role: "Compliance Manager · Responsible Infrastructure Lead",
    years: 9, favouriteProject: "Cressbrook Mill FRP Footbridge",
    responsibilities: ["Accreditations & management systems", "Carbon baseline programme", "Evidence & verification"],
    quote: "If a number is on this page, we can show you where it came from. If we can't yet, we say so.",
    image: "/team/alex-colesby.jpg"
  },
  {
    id: "laura-toy", name: "Laura Toy", initials: "LT",
    role: "Operations Assistant · Impact Stories Coordinator",
    years: 6, favouriteProject: "Droitwich Health Corridor",
    responsibilities: ["Impact Story data capture", "Document & policy control", "Reporting cadence"],
    quote: "Every fuel record, every community hour. My job is making sure the story behind the data is real.",
    image: "/team/laura-toy.jpg"
  },
  {
    id: "arron-roberts", name: "Arron Roberts", initials: "AR",
    role: "Health & Safety Manager",
    years: 11, favouriteProject: "Braunstone Culvert Replacement",
    responsibilities: ["Site safety culture", "Behavioural safety programme", "Incident learning"],
    quote: "Anyone on our sites can stop the job. That's not a poster, it's how we work.",
    image: "/team/arron-roberts.jpg"
  },
  {
    id: "duncan-braybrooke", name: "Duncan Braybrooke", initials: "DB",
    role: "Managing Director",
    years: 18, favouriteProject: "Severn Valley Railway Footbridge",
    responsibilities: ["Business strategy", "Board-level responsibility", "Client partnerships"],
    quote: "Clients don't buy an acronym. They buy how a contractor behaves when nobody's watching.",
    image: "/team/duncan-braybrooke.jpg"
  }
];

// ================= ACHIEVEMENTS (Live Statistics) =================
export const achievements = [
  { id: "a1", value: "2040", label: "Net Zero Target", sub: "Published pathway anchored to verified baseline", evidence: "Carbon Reduction Plan", tags: ["carbon"] },
  { id: "a2", value: "ISO 14001", label: "Since 2010", sub: "Environmental management, externally audited yearly", evidence: "Environmental Management Certificate", tags: ["accreditations"] },
  { id: "a3", value: "Scope 1, 2 & 3", label: "Carbon Reporting", sub: "Full baseline measurement in progress", evidence: "Carbon Baseline Methodology", tags: ["carbon", "reporting", "data"] },
  { id: "a4", value: "26,000", label: "Bottles, One Bridge", sub: "FRP structure with a 120-year design life", evidence: "Cressbrook Case Study", tags: ["materials"] },
  { id: "a5", value: "69", label: "Years of Family Ownership", sub: "Three generations on the same payroll", evidence: "Company history", tags: ["people"] },
  { id: "a6", value: "£500 – £8M", label: "Same Standards", sub: "Identical safety & environmental controls at every scale", evidence: "H&S Policy", tags: ["safety-systems"] }
];

// ================= REPORTS =================
export const reports = [
  { id: "r1", title: "Carbon Reduction Plan", summary: "PPN 06/21 format plan covering our Net Zero 2040 pathway, baseline methodology and fleet transition programme.", pages: 18, published: "2026-06-01", version: "v2.1", category: "Report", tags: ["carbon", "fleet", "reporting"] },
  { id: "r2", title: "Environmental Policy", summary: "How environmental risk is assessed, controlled and audited on every R&C scheme under ISO 14001.", pages: 6, published: "2026-05-01", version: "v4.0", category: "Policy", tags: ["accreditations", "wildlife"] },
  { id: "r3", title: "Health & Safety Policy", summary: "Our safety management system, behavioural safety programme and the stop-the-job principle in full.", pages: 12, published: "2026-04-12", version: "v6.2", category: "Policy", tags: ["safety-systems"] },
  { id: "r4", title: "Modern Slavery Statement", summary: "Annual statement covering supply chain due diligence, risk assessment and remediation commitments.", pages: 4, published: "2026-04-12", version: "2026", category: "Policy", tags: ["ethics", "supply-chain"] },
  { id: "r5", title: "Social Value Summary", summary: "Local employment, training and community impact evidence formatted for framework and tender submissions.", pages: 10, published: "2026-03-15", version: "v1.3", category: "Report", tags: ["community", "people", "training"] },
  { id: "r6", title: "Environmental Management Certificate", summary: "Current ISO 14001 certificate with scope of registration and audit history.", pages: 2, published: "2026-01-10", version: "2026", category: "Certificate", tags: ["accreditations"] }
];

// ================= TARGETS =================
export const targets = [
  { id: "t1", pillar: "Environment", title: "Net zero operations by 2040", status: "On Track", detail: "Verified baseline in measurement; fleet transition and HVO trial underway.", due: "2040", tags: ["carbon", "fleet"] },
  { id: "t2", pillar: "Environment", title: "Verified Scope 1, 2 & 3 carbon baseline", status: "On Track", detail: "Data collection live across all sites; publication follows verification.", due: "2026", tags: ["carbon", "data"] },
  { id: "t3", pillar: "Environment", title: "98% waste diverted from landfill", status: "Monitoring", detail: "Segregation at source in place; diversion rate being independently verified.", due: "2028", tags: ["waste"] },
  { id: "t4", pillar: "Social", title: "First structured apprenticeship intake", status: "Planned", detail: "College partnerships in development; first placements targeted 2027.", due: "2027", tags: ["training", "people"] },
  { id: "t5", pillar: "Social", title: "Community programme launched", status: "Planned", detail: "Charity partners and school engagement in scoping around our Aston depot.", due: "2026", tags: ["community", "charity"] },
  { id: "t6", pillar: "Governance", title: "Supplier Charter, full coverage", status: "On Track", detail: "Charter drafted and in consultation with supply chain partners.", due: "2027", tags: ["ethics", "supply-chain"] },
  { id: "t7", pillar: "Governance", title: "ISO 14001 recertification", status: "Completed", detail: "Recertified with zero non-conformances at last external audit.", due: "2026", tags: ["accreditations"] },
  { id: "t8", pillar: "Environment", title: "Electric vehicle fleet transition", status: "Awaiting Verification", detail: "First EVs in service; verified numbers to be published with fleet data.", due: "2030", tags: ["fleet", "carbon"] }
];

// ================= PROJECT EXPLORER =================
export const explorer = {
  title: "Explore a Live Scheme",
  project: "Highway Bridge Works, Midlands",
  image: assets.aerial,
  hotspots: [
    { id: "h1", x: 47, y: 34, icon: "HardHat", title: "The Structure", category: "Safety", text: "Phased night works and temporary deflection structures kept traffic flowing while the deck was refurbished, zero unplanned closures.", storyId: "safety-stand-down" },
    { id: "h2", x: 24, y: 60, icon: "Droplets", title: "The Watercourse", category: "Environment", text: "Silt fencing, spill kits and ecological watching briefs protect the water below. Nothing enters the watercourse but clean rain.", storyId: "braunstone-watercourse" },
    { id: "h3", x: 71, y: 47, icon: "Truck", title: "Site Compound", category: "Environment", text: "Solar-assisted welfare units and telematics-monitored plant reduce idling and cut depot-to-site fuel burn.", storyId: "hvo-fleet-trial" },
    { id: "h4", x: 60, y: 74, icon: "Users", title: "Site Entrance", category: "Community", text: "Resident letter drops, banksman-controlled access and school-run timing restrictions keep the neighbourhood onside.", storyId: "droitwich-active-travel" },
    { id: "h5", x: 36, y: 20, icon: "Recycle", title: "Materials Lay-down", category: "Innovation", text: "Recycled aggregates and cement-replacement concrete specified wherever design allows, logged for carbon reporting.", storyId: "cressbrook-frp" }
  ]
};

// ================= MAP =================
export const mapGroups = ["All", "Projects", "Community", "Training", "Charity", "Environmental"];

// ================= HELPERS =================
export const sortedStories = [...esgActivities].sort((a, b) => new Date(b.date) - new Date(a.date));

export const latestStory = sortedStories[0];

export const randomFeatured = () => {
  const pool = esgActivities.filter((s) => s.featured);
  return pool[Math.floor(Math.random() * pool.length)];
};

export const randomQuote = () => {
  const pool = people.filter((p) => p.quote);
  return pool[Math.floor(Math.random() * pool.length)];
};

export const randomHeroIndex = () => Math.floor(Math.random() * heroMedia.length);

export const storiesByTag = (tag) => sortedStories.filter((s) => s.tags.includes(tag));

export const storiesByPerson = (name) => sortedStories.filter((s) => s.people.includes(name));

export const relatedStories = (story, max = 3) =>
  sortedStories
    .filter((s) => s.id !== story.id)
    .map((s) => ({
      s,
      score:
        (s.project === story.project ? 3 : 0) +
        s.people.filter((p) => story.people.includes(p)).length * 2 +
        s.categories.filter((c) => story.categories.includes(c)).length +
        s.tags.filter((t) => story.tags.includes(t)).length
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map((x) => x.s);

export const relatedReports = (story, max = 2) =>
  reports.filter((r) => r.tags.some((t) => story.tags.includes(t))).slice(0, max);

export const getStory = (id) => esgActivities.find((s) => s.id === id);

// ================= GIVING BACK PAGE =================
export const giv1 = {
  name: "GIV£1",
  strapline: "A pound a week. A different local charity every month.",
  how: [
    { step: "01", title: "Staff give £1 a week", text: "Over 90% of the workforce donate £1 per week straight from payroll. Entirely voluntary, quietly powerful." },
    { step: "02", title: "R&C tops it up", text: "The company tops the pot up to £500 every single month, twelve months a year, no exceptions." },
    { step: "03", title: "Staff pick the cause", text: "Each month a member of the team nominates a local charity close to their heart to receive the full amount." }
  ],
  beneficiaries: [
    { name: "Breast Friends", area: "Sutton Coldfield", note: "The first ever GIV£1 recipient, nominated for a Senior Foreman's wife going through treatment." },
    { name: "Erdington Foodbank", area: "Erdington, Birmingham", note: "Monthly food provisions and top-ups since November 2020, our longest-running partnership." },
    { name: "Acorns Children's Hospice", area: "Walsall", note: "A new garden shed for the children's outdoor equipment, delivered with our Area 9 SRN partners." },
    { name: "Mates in Mind", area: "Construction, UK-wide", note: "Mental health in construction. Backed by the team's Three Peaks Challenge fundraising." }
  ],
  contacts: [
    { name: "Sam Drew", email: "sam.drew@randcwilliams.ltd.uk" },
    { name: "Alex Colesby", email: "alex@randcwilliams.ltd.uk" }
  ]
};

export const activeCampaign = {
  title: "National Three Peaks Challenge",
  charity: "Mates in Mind",
  storyId: "three-peaks-challenge",
  image: "https://customer-assets-39nsmqrw.emergentagent.net/job_polished-video-hero/artifacts/vw2tfn9u_3%20peaks%202.png",
  raised: 2000,
  goal: 3000,
  raisedLabel: "£2,000 target smashed",
  goalLabel: "Now pushing for £3,000",
  donateUrl: null,
  status: "awaiting"
};

export const givingStats = [
  { value: "£6,000", label: "To local causes every year", note: "£500 GIV£1 pot, every month", status: "verified" },
  { value: "90%+", label: "Of staff give every week", note: "Voluntary payroll donations", status: "verified" },
  { value: "12", label: "Charities supported a year", note: "A different cause each month", status: "verified" },
  { value: "£2,000+", label: "Raised for Mates in Mind", note: "Three Peaks Challenge 2026", status: "awaiting" }
];

export const charityStories = () =>
  sortedStories.filter((s) => s.categories.includes("Community") || s.tags.includes("charity"));

export const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
